import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { Button } from "components/button";
import { PaginationController } from "components/PaginationController/PaginationController";
import LoadingSpinner from "components/loading/LoadingSpinner";
import { useOrganisation } from "contexts/OrganisationProvider";
import { useAcpExports } from "hooks/useAcpExports";
import { toast } from "utils/toast";
import { logError } from "shared/services/ErrorReporting";
import { Text } from "components/text";
import { AcpExportItem } from "./components/AcpExportItem";

export const OrganisationExportsManualPage = () => {
  const { t } = useTranslation("settings");
  const { id: organisationId, triggerAcpExport } = useOrganisation();
  const [isExporting, setIsExporting] = useState(false);

  const { exports, loading, itemsCount } = useAcpExports({ organisationId });

  const handleStartExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      await triggerAcpExport();
      toast.success(t("organisation.exports.acp.exportRequested"));
    } catch (e) {
      logError(e as Error);
      toast.error(
        t("organisation.exports.acp.errorMessage", {
          message: (e as Error).message,
        }),
      );
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div>
      <div className="flex items-center text-primary-500 mb-6">
        <Link
          to="/settings/organisations/$organisationId/exports"
          params={{ organisationId }}
          className="flex items-center gap-2 text-sm"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("organisation.exports.back")}
        </Link>
      </div>
      <div className="flex justify-between items-center">
        <div className="grow">
          <Text as="h2" variant="pageHeading" className="mb-2">
            {t("organisation.exports.manual.title")}
          </Text>
          <Text variant="subtle" className="pb-4">
            {t("organisation.exports.manual.description")}
          </Text>
        </div>
        <div>
          <Button
            variant="primary"
            icon={isExporting ? "loader" : "download"}
            iconClassName={isExporting ? "animate-spin" : undefined}
            label={t("organisation.exports.manual.exportButton")}
            className="py-2 font-normal"
            onClick={handleStartExport}
            disabled={isExporting}
          />
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center w-full pt-10">
          <LoadingSpinner />
        </div>
      )}

      {!loading && exports.length === 0 && (
        <Text variant="subtle" className="mt-8">
          {t("organisation.exports.history.empty")}
        </Text>
      )}

      <ul className="mt-8 flex flex-col gap-2">
        {exports.map((exp) => (
          <AcpExportItem
            count={exp.count}
            key={exp.id}
            status={exp.status}
            createdAt={exp.createdAt}
            storagePath={exp.storagePath}
          />
        ))}
      </ul>

      <div className="mt-4">
        <PaginationController
          itemsCount={itemsCount}
          pageSizeLabel={t("organisation.exports.history.perPage")}
        />
      </div>
    </div>
  );
};
